import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trophy, Boxes } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Cell } from "recharts";
import api from "@/api";
import TopBar from "@/components/TopBar";
import { useLang } from "@/context/LanguageContext";

// Phase B (Feb 2026): Manager Performance full-screen page.
// Shows full salesman-performance list (incl. manager rows), sorted by Boxes Sold desc,
// with a simple horizontal bar chart at the top.
const RANK_BADGE = ["🥇", "🥈", "🥉"];
const BAR_COLORS = ["#FFC107", "#9CA3AF", "#CD7F32", "#0055FF", "#5856D6", "#34C759", "#FF9500"];

export default function ManagerPerformance() {
  const nav = useNavigate();
  const { t } = useLang();
  const [data, setData] = useState({ salesmen: [], today: {} });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/dashboard/manager")
      .then(r => setData(r.data))
      .finally(() => setLoading(false));
  }, []);

  // Backend already sorts by units_sold desc.
  const rows = data.salesmen || [];
  const top = rows.slice(0, 7);

  return (
    <div className="min-h-screen bg-[#F5F5F7] pb-10 page-slide-in">
      <TopBar title={t("salesman_performance")} onBack={() => nav("/manager")} />
      <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-5 fade-up">

        {/* Top performers ranking */}
        <div className="bg-gradient-to-br from-[#0A1A3F] to-[#0055FF] text-white rounded-3xl p-6 shadow-xl" data-testid="mp-best-card">
          <div className="flex items-center gap-2 mb-4">
            <Trophy className="w-6 h-6 text-yellow-300" />
            <div className="heading-font font-bold text-xl">{t("best_sales_today")}</div>
          </div>
          {rows.length === 0 ? (
            <div className="text-white/70 text-sm">No data yet</div>
          ) : (
            <div className="space-y-2">
              {rows.slice(0, 3).map((r, i) => (
                <div key={r.id} className="flex items-center justify-between bg-white/10 rounded-2xl p-3" data-testid={`mp-rank-${i+1}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="text-2xl">{RANK_BADGE[i]}</span>
                    <div className="min-w-0">
                      <div className="font-bold heading-font truncate">{r.name}</div>
                      <div className="text-xs text-white/70">{r.delivered || 0} delivered • {r.pending || 0} pending</div>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="heading-font text-2xl font-bold">{r.units_sold || 0}</div>
                    <div className="text-[10px] uppercase tracking-wider opacity-70">{t("boxes")}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Bar chart */}
        {top.length > 0 && (
          <div className="bg-white rounded-2xl p-5 soft-shadow" data-testid="mp-chart-card">
            <div className="flex items-center gap-2 mb-3">
              <Boxes className="w-5 h-5 text-[#0055FF]" />
              <h3 className="heading-font font-bold text-lg">{t("boxes_sold")}</h3>
            </div>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={top} layout="vertical" margin={{ left: 8, right: 16, top: 6, bottom: 6 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis type="number" tick={{ fontSize: 12, fill: "#86868B" }} allowDecimals={false} />
                  <YAxis dataKey="name" type="category" width={140} tick={{ fontSize: 12, fill: "#1D1D1F" }} />
                  <Tooltip
                    contentStyle={{ borderRadius: 12, border: "1px solid #eee" }}
                    formatter={(v) => [`${v} ${t("boxes")}`, t("boxes_sold")]}
                  />
                  <Bar dataKey="units_sold" radius={[0, 8, 8, 0]}>
                    {top.map((_, i) => (
                      <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Full table */}
        <div className="bg-white rounded-2xl soft-shadow overflow-hidden">
          <div className="p-5 border-b border-gray-100">
            <h2 className="heading-font font-bold text-xl">{t("salesman_performance")}</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50/60">
                <tr className="text-left text-[#86868B] uppercase tracking-wider text-xs">
                  <th className="px-4 py-3">{t("rank")}</th>
                  <th className="px-4 py-3">{t("name")}</th>
                  <th className="px-4 py-3">{t("orders")}</th>
                  <th className="px-4 py-3">{t("delivered_today")}</th>
                  <th className="px-4 py-3">{t("pending")}</th>
                  <th className="px-4 py-3">{t("boxes_sold")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((s, i) => (
                  <tr key={s.id} className="hover:bg-gray-50/40" data-testid={`mp-row-${s.id}`}>
                    <td className="px-4 py-3 font-bold text-[#86868B]">
                      {i < 3 ? <span className="text-xl">{RANK_BADGE[i]}</span> : `#${i + 1}`}
                    </td>
                    <td className="px-4 py-3 font-semibold heading-font">
                      {s.name}
                      {s.role === "manager" && (
                        <span className="pill bg-blue-50 text-blue-700 text-[10px] ml-1.5">Mgr</span>
                      )}
                    </td>
                    <td className="px-4 py-3">{s.total_orders || 0}</td>
                    <td className="px-4 py-3"><span className="pill bg-green-50 text-green-700">{s.delivered || 0}</span></td>
                    <td className="px-4 py-3"><span className="pill bg-orange-50 text-orange-700">{s.pending || 0}</span></td>
                    <td className="px-4 py-3 font-bold text-[#1D1D1F]">{s.units_sold || 0}</td>
                  </tr>
                ))}
                {!loading && rows.length === 0 && (
                  <tr><td colSpan="6" className="px-4 py-8 text-center text-[#86868B]">No data</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
